import dayjs from 'dayjs';
import { Button } from '@/components/UI';
import { ruleRoleOptions } from './constants';
import { getRuleRoleClassName } from './helpers';
import type { RuleGroupRow, ScorecardRuleRole, ScorecardRuleRow } from './types';

interface RuleGroupSubTableProps {
    group: RuleGroupRow;
    deletingRuleId: number | null;
    onToggleRuleEnabled: (ruleId: number, enabled: boolean) => Promise<void>;
    onUpdateRuleRole: (ruleId: number, ruleRole: ScorecardRuleRole) => Promise<void>;
    onRequestDelete: (rule: ScorecardRuleRow) => void;
}

const RuleGroupSubTable = ({ group, deletingRuleId, onToggleRuleEnabled, onUpdateRuleRole, onRequestDelete }: RuleGroupSubTableProps) => {
    return (
        <div className="p-3">
            <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                    <thead>
                        <tr className="text-zinc-300">
                            <th className="text-left p-2">ID</th>
                            <th className="text-left p-2">Enabled</th>
                            <th className="text-left p-2">Role</th>
                            <th className="text-left p-2">Selector</th>
                            <th className="text-left p-2">Lifts (Stock/Option %)</th>
                            <th className="text-left p-2">Sample Size</th>
                            <th className="text-left p-2">Updated</th>
                            <th className="text-left p-2">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {group.rules.map((r) => {
                            let selectorStr: string;

                            try {
                                selectorStr = r.selector ? JSON.stringify(JSON.parse(r.selector)) : '';
                            } catch {
                                selectorStr = r.selector || '';
                            }

                            let metrics: string;

                            try {
                                const parts: string[] = [];
                                if (r.stockLift) parts.push(`stock ${Number(r.stockLift).toFixed(2)}%`);
                                if (r.optionLift) parts.push(`option ${Number(r.optionLift).toFixed(2)}%`);
                                metrics = parts.join(' | ');
                            } catch {
                                metrics = '';
                            }

                            return (
                                <tr className="border-t border-zinc-800 text-zinc-200" key={r.id}>
                                    <td className="p-2">{r.id}</td>
                                    <td className="p-2">
                                        <input
                                            aria-label={`Toggle rule ${r.id}`}
                                            checked={!!r.enabled}
                                            className="cursor-pointer"
                                            type="checkbox"
                                            onChange={(e) => onToggleRuleEnabled(r.id, e.target.checked)}
                                        />
                                    </td>
                                    <td className="p-2">
                                        <select
                                            aria-label={`Rule ${r.id} role`}
                                            className={`rounded border px-2 py-1 text-xs ${getRuleRoleClassName(r.ruleRole)}`}
                                            value={r.ruleRole}
                                            onChange={(event) => onUpdateRuleRole(r.id, event.target.value as ScorecardRuleRole)}
                                        >
                                            {ruleRoleOptions.map((option) => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    </td>
                                    <td className="p-2 font-mono text-xs break-all max-w-[360px]">{selectorStr}</td>
                                    <td className="p-2">{metrics}</td>
                                    <td className="p-2">{r.sampleSize}</td>
                                    <td className="p-2">{r.updatedAt ? dayjs(r.updatedAt).format('YYYY-MM-DD HH:mm') : ''}</td>
                                    <td className="p-2">
                                        <Button
                                            disabled={deletingRuleId === r.id}
                                            size="sm"
                                            variant="danger"
                                            onClick={() => onRequestDelete(r)}
                                        >
                                            Remove
                                        </Button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default RuleGroupSubTable;
